import React, { useCallback } from "react";
import PropTypes from "prop-types";

import Tooltip from "components/Tooltip";

import { Controller, useFormField, useGetFieldType } from "./hooks";
import {
  Button,
  FieldDetailsModal,
  FieldOptions,
  FieldTypeSelector,
  Form,
  FormInput,
  Icon,
  TaskIcon,
  useBooleanState,
} from "./components";

export function FormFieldBuilder({
  loading,
  initialValues,
  formName,
  onSubmit,
  onClose,
}) {
  const { loadingFieldType, fieldTypes, error } = useGetFieldType();
  const { selectedType, formProps, handleSubmit, isReady, setFieldDetails } =
    useFormField(initialValues, onSubmit);
  const [
    isFieldDetailsModalOpened,
    openFieldDetailsModal,
    closeFieldDetailsModal,
  ] = useBooleanState();

  const getSubtitle = useCallback(() => {
    const formFieldName = formProps.getValues().name;

    if (formName && formFieldName) {
      return `${formName} / ${formFieldName}`;
    }

    return formName || formFieldName || null;
  }, [formName, formProps]);

  if (error) return <div data-test="field-type-loading-error">{error}</div>;

  return (
    <React.Fragment>
      <Form {...formProps} onSubmit={handleSubmit}>
        <Tooltip
          title="This field is associated to one or more conditions. Please remove the associated conditions before editing."
          hidden={!initialValues.disableEdit}
        >
          <span>
            <Controller
              name="type"
              render={({ field }) => {
                return (
                  <FieldTypeSelector
                    disabled={
                      loadingFieldType || loading || initialValues.disableEdit
                    }
                    loading={loadingFieldType}
                    style={{ marginBottom: "0.5em" }}
                    types={fieldTypes}
                    initialValue={field.value}
                    onChange={(data) => {
                      formProps.resetField("defaultValue", {
                        defaultValue:
                          data.id === "MULTISELECT_DROPDOWN" ? [] : null,
                      });
                      field.onChange(data);
                    }}
                    onBlur={field.onBlur}
                  />
                );
              }}
            />
          </span>
        </Tooltip>
        {selectedType && (
          <FormInput
            data-test="name-input"
            name="name"
            fluid
            placeholder="Add field name"
            disabled={loading}
            style={{ marginBottom: "0.5em", opacity: loading ? 0.45 : 1 }}
          />
        )}
        {selectedType?.showOptions && (
          <Controller
            name="options"
            render={({ field }) => {
              return (
                <FieldOptions
                  disabled={loading}
                  initialValue={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  formSubmitted={formProps.formState.isSubmitted}
                  style={{ marginBottom: "0.5em" }}
                />
              );
            }}
          />
        )}
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ flexGrow: 1 }}>
            <Button
              content={initialValues?.type ? "Save" : "Add"}
              positive
              loading={loading}
              disabled={!isReady || loading}
            />
            <Icon
              data-test="close-icon"
              disabled={loading}
              link
              name="delete"
              color="grey"
              size="large"
              onClick={onClose}
            />
          </div>
          {selectedType && (
            <div data-test="edit-field-details-icon">
              <TaskIcon
                iconName="align left"
                iconNameOutline="align left"
                title="Edit Field Details"
                onClick={openFieldDetailsModal}
                hideAddIcon
              />
            </div>
          )}
        </div>
      </Form>
      {isFieldDetailsModalOpened && (
        <FieldDetailsModal
          subtitle={getSubtitle()}
          field={formProps.getValues()}
          onClose={closeFieldDetailsModal}
          onSave={(data) => {
            setFieldDetails(data);
            closeFieldDetailsModal();
          }}
        />
      )}
    </React.Fragment>
  );
}

FormFieldBuilder.propTypes = {
  loading: PropTypes.bool,
  initialValues: PropTypes.PropTypes.shape({
    type: PropTypes.object,
    name: PropTypes.string,
    options: PropTypes.array,
    required: PropTypes.bool,
    description: PropTypes.string,
    disableEdit: PropTypes.bool,
    defaultValue: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.instanceOf(Date),
    ]),
  }),
  formName: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

FormFieldBuilder.defaultProps = {
  loading: false,
  formName: "Form",
  initialValues: { type: null, name: "", options: [], required: true },
};

export default FormFieldBuilder;
